import { ArrowUp } from 'lucide-react'

/**
 * SendButton — composer send affordance shared by the agentic-hub chat surfaces
 * (SearchAgentPanel hero, SearchAgentPanel reply composer, ChatPill input bar).
 *
 * Visual contract (Numeo DS):
 *   - Round button (default `md` = 32×32, `sm` = 28×28).
 *   - bg-blue-500 → bg-blue-600 on hover (active/brand color).
 *   - When `disabled`, the surface fades to slate so the empty-input state is
 *     clearly inert. Disabled is driven by the caller (typically `!input.trim()
 *     || isStreaming`) rather than computed here, because send semantics depend
 *     on each composer's flow.
 *   - Icon: lucide ArrowUp (the familiar composer send affordance). Sized to
 *     match the surrounding composer typography across both size variants.
 */

export interface SendButtonProps {
    onClick: () => void
    disabled?: boolean
    /** When true, button is in a transient "send-in-flight" state — aria-busy true + still disabled */
    loading?: boolean
    size?: 'sm' | 'md'
    'aria-label'?: string
    className?: string
}

const sizeClasses: Record<NonNullable<SendButtonProps['size']>, { box: string; icon: number }> = {
    sm: { box: 'w-7 h-7', icon: 13 },
    md: { box: 'w-8 h-8', icon: 14 },
}

export const SendButton = ({ onClick, disabled = false, loading = false, size = 'md', 'aria-label': ariaLabel = 'Send', className = '' }: SendButtonProps) => {
    const tokens = sizeClasses[size]
    const isInert = disabled || loading
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={isInert}
            aria-disabled={isInert}
            aria-busy={loading}
            aria-label={ariaLabel}
            className={`${tokens.box} rounded-full ${loading ? 'bg-blue-500' : 'bg-blue-500 hover:bg-blue-600'} disabled:bg-slate-200 dark:disabled:bg-slate-700/40 flex items-center justify-center flex-shrink-0 transition-colors ${className}`}
        >
            {loading ? (
                // Inline spinner — appears during the send → AH_StreamStart window. Same
                // visual weight as the send icon so the button doesn't visually shift.
                <svg
                    className="animate-spin text-white"
                    style={{ width: tokens.icon, height: tokens.icon }}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                >
                    <circle cx="12" cy="12" r="9" strokeOpacity="0.25" />
                    <path strokeLinecap="round" d="M21 12a9 9 0 0 0-9-9" />
                </svg>
            ) : (
                <ArrowUp size={tokens.icon + 2} strokeWidth={2.5} className={isInert ? 'text-slate-400 dark:text-slate-500' : 'text-white'} />
            )}
        </button>
    )
}
